import ProductsApi from "@/scripts/api/products.api";
import {ReviewFormProps} from "@/scripts/components/review-form/reviewForm.types";
import {getForm, getIntro} from "@/scripts/components/review-form/helpers/reviewFormHtmlFunctions";
import {ProductReviewRequestDTO} from "@shared/DTO/ProductReviewRequestDTO";
import AB_FORM_NAMES from "@/scripts/components/ab-form/constants/abFormNames.constants";
import {REVIEW_FORM_FIELDS} from "@/scripts/components/review-form/constants/reviewForm.constants";
import {ProductResponseDTO, ProductReviewResponseDTO} from "@shared/DTO";

export class ReviewFormController {

  constructor(
    private rootEl: HTMLElement,
    private props: {productIdWp?: string; productId?: string} = {}
  ) {
  }

  private _productApiService = ProductsApi;

  get productId() {
    return this.props?.productId;
  }
  set productId(v: string | undefined) {
    this.props.productId = v;
  }

  mount() {
    if(!this.productId && !this.props?.productIdWp) return;
    this.reload(); // primo load
  }

  unmount() {
    // nothing so far
  }

  updateProps(next: Partial<ReviewFormProps>) {
    const prev = this.props;
    this.props = {...this.props, ...next};
    if (next.productId && next.productId !== prev.productId) {
      this.reload();
    }
  }

  async reload() {
    this.rootEl.replaceChildren(); // svuota tutto

    // IF qui sotto: durante la fase di transizione, per...
    // ...permettere di ricercare prodotto tramite id WP

    let product: ProductResponseDTO;
    let reviews: ProductReviewResponseDTO[];
    if(this.productId) {
      [product, reviews] = await Promise.all([
        this.fetchProduct(this.productId),
        this.fetchReviews(this.productId)
      ]);
    } else  {
      product = await this.fetchProduct(`wp/${this.props.productIdWp}`);
      this.productId = product?._id;
      if(product) {
        reviews = await this.fetchReviews(product._id);
      }
    }

    if(!product) return;

    const {heading, paragraph} = getIntro(product,reviews) || {};
    if(heading && paragraph) {
      this.rootEl.append(heading, paragraph);
    }
    const form = getForm(product) as HTMLFormElement;

    this.rootEl.append(form);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      this.submit(form);
    });

    form.addEventListener("input", async (e) => {
      e.preventDefault();
      this.formChanged(form);
    });

    form.addEventListener("change", async (e) => {
      e.preventDefault();
      this.formChanged(form);
    });

  }

  private formChanged(form: HTMLFormElement) {
    const classInvalid = AB_FORM_NAMES.CLASS_INVALID;
    const inputsInvalid = form.querySelectorAll(`.${classInvalid}`);
    inputsInvalid?.forEach((el) => el.classList.remove(classInvalid));
  }

  private async submit(form: HTMLFormElement) {
    if (!this.productId) return;

    let hasErrors = false;

    const {CLASS_FORM_CONTROL, CLASS_INVALID, CLASS_FORM_INPUT, CLASS_FORM_SUBMIT} = AB_FORM_NAMES;

    const submitButtonElement = form.querySelector(`.${CLASS_FORM_SUBMIT}`) as HTMLButtonElement;

    const authorNameControl = form.querySelector(`.${CLASS_FORM_CONTROL}--${REVIEW_FORM_FIELDS.NAME}`);

    const authorNameInput = authorNameControl.querySelector(`.${CLASS_FORM_INPUT}`) as HTMLInputElement;
    const authorName = authorNameInput?.value?.trim();
    if (!authorName?.length) {
      hasErrors = true;
      authorNameControl?.classList.add(CLASS_INVALID);
    }

    const authorEmailControl = form.querySelector(`.${CLASS_FORM_CONTROL}--${REVIEW_FORM_FIELDS.EMAIL}`);
    const authorEmailInput = authorEmailControl.querySelector(`.${CLASS_FORM_INPUT}`) as HTMLInputElement;
    const authorMail = authorEmailInput?.value?.trim();
    if (!authorMail?.length) {
      hasErrors = true;
      authorEmailControl?.classList.add(CLASS_INVALID);
    }

    const contentControl = form.querySelector(`.${CLASS_FORM_CONTROL}--${REVIEW_FORM_FIELDS.CONTENT}`);
    const contentTextArea = contentControl.querySelector(`.${CLASS_FORM_INPUT}`) as HTMLTextAreaElement;
    const content = contentTextArea?.value?.trim();
    if (!content?.length || content.length < 40) {
      hasErrors = true;
      contentControl?.classList.add(CLASS_INVALID);
    }

    const ratingControl = form.querySelector(`.${CLASS_FORM_CONTROL}--${REVIEW_FORM_FIELDS.RATING}`);
    const abStarsEl = ratingControl.querySelector(`.${CLASS_FORM_INPUT}`) as HTMLElement;
    const rating = parseInt(abStarsEl?.getAttribute("rating") || "0", 10) || 0;
    if (rating <= 0) {
      hasErrors = true;
      ratingControl?.classList.add(CLASS_INVALID);
    }

    if (hasErrors) return;

    const review: ProductReviewRequestDTO = {
      authorMail,
      authorName,
      content,
      rating,
    };

    form.classList.add(AB_FORM_NAMES.CLASS_FORM_SUBMITTED);

    this._productApiService.submitReview(this.productId, review)
      .then((res) => {
        form.querySelector(`.${AB_FORM_NAMES.CLASS_FORM_FEEDBACK__SUCCESS}`).textContent = `Grazie! La tua recensione è stata inviata ed è in attesa di approvazione. Sarà pubblicata a breve.`
      })
      .catch((err) => {
        console.error("Error submitting review", err);
        form.querySelector(`.${AB_FORM_NAMES.CLASS_FORM_FEEDBACK__ERROR}`).textContent = `A causa di un problema tecnico non è stato possibile inviare la recensione.`
    })
      .finally(() => {
        [
          authorNameInput,
          authorEmailInput,
          contentTextArea,
          abStarsEl,
          submitButtonElement
        ].forEach((el) => {
          el.setAttribute("disabled", "true");
        });
      })
  }

  private async fetchProduct(productId: string) {
    try {
      if (!productId) {
        throw new Error("Product ID is required to fetch product details.");
      }
      return await this._productApiService.getProduct(productId);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      return null;
    }
  }

  private async fetchReviews(productId: string) {
    try {
      return await this._productApiService.getReviews(productId);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      return [];
    }
  }

}